import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface RecommendationSelectorProps {
  onSelectQuick: () => void;
  onSelectAdvanced: () => void;
}

const RecommendationSelector: React.FC<RecommendationSelectorProps> = ({
  onSelectQuick,
  onSelectAdvanced,
}) => {
  const { user } = useAuth();
  const [requestSent, setRequestSent] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const handleRequestAdvanced = async () => {
    setRequesting(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/v1/auth/request-advanced-access`);
      toast.success(data.message || 'Request submitted.');
      setRequestSent(true);
    } catch (err: any) {
      toast.error(
        err.response?.data?.detail || "Couldn't submit that request — try again in a moment."
      );
    } finally {
      setRequesting(false);
    }
  };

  const hasAdvancedAccess = user?.is_advanced_user || user?.is_superuser;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-6 sm:py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3">
            Choose Your Path
          </h1>
          <p className="text-base sm:text-xl text-gray-600">
            Select the level of detail for your equipment recommendations
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Quick Recommendation */}
          <div
            onClick={onSelectQuick}
            className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 cursor-pointer transform transition-all hover:scale-105 hover:shadow-2xl border-2 border-transparent hover:border-green-500"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <span className="text-4xl">⚡</span>
              </div>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">Quick Recommendation</h2>
              <p className="text-sm text-green-600 font-medium uppercase tracking-wide">
                Available to All Users
              </p>
            </div>

            <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
              <div className="flex items-start">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  <strong>15 essential fields</strong> - Quick and easy
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  <strong>5-10 minutes</strong> to complete
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  Basic recommendations based on <strong>handicap, swing speed, and budget</strong>
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">Perfect for beginners and recreational golfers</span>
              </div>
            </div>

            <button className="w-full py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition-colors">
              Start Quick Recommendation →
            </button>
          </div>

          {/* Advanced Recommendation */}
          <div
            className={`bg-white rounded-2xl shadow-xl p-5 sm:p-8 border-2 transform transition-all relative ${
              hasAdvancedAccess
                ? 'cursor-pointer hover:scale-105 hover:shadow-2xl border-transparent hover:border-blue-500'
                : 'opacity-75 border-gray-300'
            }`}
            onClick={hasAdvancedAccess ? onSelectAdvanced : undefined}
          >
            {/* Lock Overlay */}
            {!hasAdvancedAccess && (
              <div className="absolute top-4 right-4">
                <div className="bg-gray-100 p-3 rounded-full">
                  <span className="text-3xl">🔒</span>
                </div>
              </div>
            )}

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <span className="text-4xl">🎯</span>
              </div>
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">Advanced Fitting</h2>
              <p className="text-sm text-blue-600 font-medium uppercase tracking-wide">
                {hasAdvancedAccess ? 'Unlocked' : 'Request Access Required'}
              </p>
            </div>

            <div className="space-y-2 sm:space-y-4 mb-4 sm:mb-6">
              <div className="flex items-start">
                <span className="text-blue-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  A recommendation built on <strong>your actual swing and ball-flight numbers</strong>, not just handicap and budget
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  <strong>15-20 minutes</strong>, most of it optional — pick how deep you go
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  Complete swing data, ball flight metrics, physical profile, and preferences
                </span>
              </div>
              <div className="flex items-start">
                <span className="text-blue-500 mr-3 text-xl">✓</span>
                <span className="text-gray-700">
                  <strong>Precision recommendations</strong> for serious golfers
                </span>
              </div>
            </div>

            {hasAdvancedAccess ? (
              <button className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors">
                Start Advanced Fitting →
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestAdvanced();
                }}
                disabled={requestSent || requesting}
                className={`w-full py-3 font-bold rounded-lg transition-colors ${
                  requestSent
                    ? 'bg-green-100 text-green-700 border-2 border-green-300'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 border-2 border-gray-300 disabled:opacity-60'
                }`}
              >
                {requestSent ? (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">✓</span> Request submitted
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">🔓</span> {requesting ? 'Sending…' : 'Request Advanced Access'}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Info Footer */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-3xl mx-auto">
            <h3 className="font-bold text-gray-900 mb-2">Why Request Advanced Access?</h3>
            <p className="text-gray-700 text-sm">
              Advanced fitting provides professional-grade recommendations based on comprehensive
              swing data, ball flight characteristics, physical attributes, and detailed
              preferences. Perfect for competitive golfers or those seeking precision equipment
              matching. Advanced access is granted by an administrator, not automatically —
              submitting a request lets them know you'd like it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationSelector;
